import type {
    ComposableOptionsBase,
    Product,
    ProductMarket,
    ProductCategory,
    ProductCategoryRequestParams,
    ColorOption,
    IProductVersionItem
} from '~/types';
import type {
    CustomPagination,
    PaginatedPayload,
    PaginatedRequest,
    ApiQueryParams
} from 'x1-base/types';
import { useApiUtils } from 'x1-base/composables';
import { useCustomFetch } from '~/composables';
import { omit as lo_omit } from 'es-toolkit';
import { merge as lo_mergeCompat } from 'es-toolkit/compat';
import { isEmpty as lo_isEmpty } from 'es-toolkit/compat';
import type { NitroFetchOptions } from 'nitropack';
import { ProductVersions } from '~/constants';


export const productBaseExpanders = [
    'skus.component_group',
    'skus.images',
    'skus.attribute_values',
    'product.defaultImage',
    'product_attributes',
    'product_attributes.attribute',
    'product_attributes.values',
    'availability',
    'component_groups',
    'features',
    'serial_code_versions',
    'category',
    'category.warranty_policy',
    'services',
    'retail_urls',
    'product_retailers.org',
    'discount',
    'retail_urls.org.brand.primary_logo',
    'retail_urls.attribute_values',
    'component_set',
    'garment_sizes',
    'total_reviews',
    'avg_reviews'
];

export interface UseProductsOptions extends ComposableOptionsBase {}
export function useProducts(options: Partial<UseProductsOptions> = {}) {
    const apiBasePath = `/v1`;
    const useApiUtilsObj = useApiUtils();
    const $_fetch = useCustomFetch({ siteContext: options.siteContext });

    async function getMarkets(args?: PaginatedRequest): Promise<PaginatedPayload<ProductMarket> | Error> {
        const url = `${apiBasePath}/markets/`;
        const config: NitroFetchOptions<string> = {};

        if (args?.params) {
            config.query = args?.params;
        }

        let paginatedPayload: PaginatedPayload<ProductMarket>;

        try {
            const response = await $_fetch<CustomPagination<ProductMarket>>(url, config);

            paginatedPayload = {
                pagination: lo_omit(response, ['results']),
                data: response.results
            };

            return paginatedPayload;
        } catch (err) {
            return useApiUtilsObj.getErrorObj(err);
        }
    }

    async function getCategories(
        args?: PaginatedRequest<ProductCategory, ProductCategoryRequestParams>
    ): Promise<PaginatedPayload<ProductCategory> | Error> {
        const url = `${apiBasePath}/categories/`;
        const config: NitroFetchOptions<string> = {};

        if (args?.params) {
            config.query = args?.params;
        }

        let paginatedPayload: PaginatedPayload<ProductCategory>;

        try {
            const response = await $_fetch<CustomPagination<ProductCategory>>(url, config);

            paginatedPayload = {
                pagination: lo_omit(response, ['results']),
                data: response.results
            };

            return paginatedPayload;
        } catch (err) {
            return useApiUtilsObj.getErrorObj(err);
        }
    }

    async function getProducts(
        args: PaginatedRequest = {},
        query: NitroFetchOptions<string>['query'] = {}
    ): Promise<PaginatedPayload<Product> | Error> {
        const url = args?.url ?? `${apiBasePath}/products/`;

        const config: NitroFetchOptions<string> = {
            method: 'GET',
            query: {}
        };

        lo_mergeCompat(config.query, args?.params, query, {
            // Base params here
        });

        let paginatedPayload: PaginatedPayload<Product>;

        try {
            const response = await $_fetch<CustomPagination<Product>>(url, config);

            paginatedPayload = {
                pagination: lo_omit(response, ['results']),
                data: response.results
            };

            return paginatedPayload;
        } catch (err) {
            return useApiUtilsObj.getErrorObj(err);
        }
    }

    async function getProduct(productUid: string, expand: string[] = [], args: ApiQueryParams = {}): Promise<Product | Error> {
        const url = `${apiBasePath}/products/${productUid}/`;
        expand = !lo_isEmpty(expand) ? expand : productBaseExpanders;
        const config: NitroFetchOptions<string> = {
            method: 'GET',
            query: Object.assign({
                expand: [...expand].join(',')
            }, args)
        };

        try {
            return await $_fetch<Product>(url, config);
        } catch (err) {
            return useApiUtilsObj.getErrorObj(err);
        }
    }

    function getSerialCodeVersionObj(product: Partial<Product>): Nullable<IProductVersionItem> {
        if (product.serialCodeVersions?.[0]) {
            const version = product.serialCodeVersions?.[0];
            const vObj = ProductVersions[product.serialCodeVersions[0]];

            if (version !== 'v0' && vObj) {
                return vObj;
            }
        }

        return null;
    }

    return {
        getMarkets,
        getCategories,
        getProduct,
        getProducts,
        getSerialCodeVersionObj
    };
}
